<template>
    <Form
        ref="addAccountForm"
        class="add-platform-account"
        :rules="validationRules"
        :model="formData"
        @submit.prevent="onFormSubmit"
    >
        <FormItem ref="accountName" label="Account name" prop="accountName">
            <Input
                v-model="formData.accountName"
                auto-complete="off"
                data-testid="add-platform-account__name"
                placeholder="Enter an account name"
            />
        </FormItem>

        <FormItem ref="accountId" label="Account ID" prop="accountId">
            <Input
                v-model="formData.accountId"
                auto-complete="off"
                data-testid="add-platform-account__id"
                placeholder="Enter your account ID"
            />
        </FormItem>

        <FormItem ref="pageId" label="Page ID" prop="pageId">
            <Input
                v-model="formData.pageId"
                auto-complete="off"
                data-testid="add-platform-account__page-id"
                placeholder="Enter your page ID"
                @input="debouncedGetInstagramAccount"
            />
        </FormItem>

        <div v-if="showInstagramAccountInfo">
            <div class="instagram-account-info">
                <div v-if="instagramAccountMessage" class="instagram-account-info__message">
                    <icon type="md-information-circle" size="20" color="#8B919A" />
                    <span v-html="instagramAccountMessage" />
                </div>
                <div v-else class="instagram-account-info__message">
                    <InstagramLogo class="instagram-logo" />
                    <div class="instagram-account-details">
                        <p>One <strong>Instagram Account</strong> found linked to this Meta Account</p>
                        <p>
                            Name: <strong>{{ instagramAccount.name }}</strong>
                        </p>
                        <p>
                            Username: <strong>{{ instagramAccount.username }}</strong>
                        </p>
                        <p>
                            User ID: <strong>{{ instagramAccount.id }}</strong>
                        </p>
                    </div>
                </div>
            </div>
            <div v-if="!instagramAccountMessage" class="instagram-checkbox">
                <Checkbox id="publishToInstagram" v-model="publishToInstagram">
                    Also publish Ad to linked Instagram Account.
                </Checkbox>
            </div>
        </div>
        <div class="form-actions">
            <Button
                :disabled="fetchingInstagramAccount"
                type="primary"
                data-testid="new-platform-account__modal-button"
                @click="onFormSubmit"
            >
                {{ actionLabel }}
            </Button>
        </div>
    </Form>
</template>

<script lang="ts">
import { defineComponent, type PropType } from "vue";
import { mapStores } from "pinia";

import { checkEmptySpaces, checkOnlyNumbers, debounce } from "@/utils";
import { useConnectStore } from "@/Connect/store";
import { getInstagramAccount } from "@/utils/CreativeIntelligenceApi/connect";
import InstagramLogo from "@/assets/InstagramLogo.svg";

export type MetaAccountFormData = {
    id?: number;
    accountName: string;
    accountId: string;
    instagramUserId: string;
    pageId: string;
};

type AddPlatformAccountInstance = {
    formData: {
        pageId: string;
    };
    showInstagramAccountInfo: boolean;
    instagramAccountMessage: string;
    getInstagramAccount: () => void;
};

export default defineComponent({
    components: {
        InstagramLogo
    },

    props: {
        actionLabel: {
            type: String
        },
        initialFormData: {
            type: Object as PropType<MetaAccountFormData>
        }
    },

    emits: ["submitFormData"],

    data() {
        return {
            formData: {
                accountName: this.initialFormData?.accountName || "",
                accountId: this.initialFormData?.accountId || "",
                instagramUserId: this.initialFormData?.instagramUserId || "",
                pageId: this.initialFormData?.pageId || ""
            },
            instagramAccount: {
                id: "",
                username: "",
                name: ""
            },
            showInstagramAccountInfo: false,
            fetchingInstagramAccount: false,
            instagramAccountMessage: "",
            publishToInstagram: !!this.initialFormData?.instagramUserId
        };
    },

    computed: {
        ...mapStores(useConnectStore),
        validationRules() {
            return {
                accountName: [
                    {
                        required: true,
                        message: "Please enter an account name",
                        trigger: "blur"
                    },
                    {
                        message: "Account name must not start with space",
                        validator: (rule, value) => checkEmptySpaces(value),
                        trigger: "blur"
                    },
                    {
                        message: "Account name must be unique",
                        validator: (rule, value) => {
                            return this.connectStore?.accounts?.every(
                                account =>
                                    account.platformAccount.name !== value ||
                                    account.platformAccount.id === this.initialFormData?.id
                            );
                        }
                    }
                ],
                accountId: [
                    {
                        required: true,
                        message: "Please enter an Account ID.",
                        trigger: "blur"
                    },
                    {
                        message: "Account ID must contain only numbers",
                        validator: (rule, value: string) => checkOnlyNumbers(value),
                        trigger: "blur"
                    }
                ],
                pageId: [
                    {
                        required: true,
                        message: "Please enter a Page ID.",
                        trigger: "blur"
                    },
                    {
                        message: "Page ID must contain only numbers",
                        validator: (rule, value: string) => checkOnlyNumbers(value),
                        trigger: "blur"
                    }
                ]
            };
        }
    },

    watch: {
        publishToInstagram(newValue) {
            this.formData.instagramUserId = newValue ? this.instagramAccount.id : "";
        }
    },

    created() {
        this.getInstagramAccount();
    },

    methods: {
        onFormSubmit() {
            this.$refs.addAccountForm.validate((valid: boolean) => {
                if (!valid) {
                    this.$snackbar.error("Please correctly fill in the required fields");
                    return;
                }

                this.$emit("submitFormData", this.formData);
            });
        },

        debouncedGetInstagramAccount: debounce(function (this: AddPlatformAccountInstance) {
            this.showInstagramAccountInfo = false;
            this.instagramAccountMessage = "";

            const pageId = this.formData.pageId?.trim();
            if (!pageId || pageId.length < 3) {
                return;
            }

            this.getInstagramAccount();
        }, 800),

        async getInstagramAccount() {
            const pageId = this.formData.pageId?.trim();

            if (!pageId) {
                return;
            }

            this.fetchingInstagramAccount = true;
            this.showInstagramAccountInfo = true;
            this.instagramAccountMessage = "Fetching Instagram account info...";

            try {
                const response = await getInstagramAccount({ campaignId: pageId });

                const { id = "", username = "", name = "" } = response?.data || {};

                if (id) {
                    this.instagramAccount = { id, username, name };
                    this.publishToInstagram = this.initialFormData?.instagramUserId === id;
                    this.instagramAccountMessage = "";
                } else {
                    this.instagramAccountMessage = `No <strong>Instagram Account</strong> found associated with this Page ID.`;
                    this.publishToInstagram = false;
                }
            } catch {
                this.instagramAccountMessage = `No <strong>Instagram Account</strong> found associated with this Page ID.`;
            } finally {
                this.fetchingInstagramAccount = false;
            }
        }
    }
});
</script>

<style lang="scss" scoped>
.instagram-account-info {
    display: flex;
    align-items: center;
    margin-bottom: 16px;
    background-color: #eff2f5;
    padding: 10px;
    border-radius: 10px;
    color: #545c62;
    font-size: 12px;
    line-break: 20px;

    &__message {
        display: flex;
        align-items: center;
        gap: 10px;
        flex: 1;
    }
}

.instagram-logo {
    width: 28px;
    height: 28px;
}

.instagram-account-details {
    flex: 1;
}

.instagram-checkbox {
    padding-bottom: 20px;
    border-bottom: 1px solid #e8e8e8;
}

.form-actions {
    margin-top: 20px;
}
</style>
